import React, { useState, useEffect } from 'react';

const CategoryForm = ({ onSubmit, onCancel, initialData = null, onFormChange }) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    description: initialData?.description || ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if form has changed from initial data
  useEffect(() => {
    if (onFormChange) {
      const hasChanges = Object.keys(formData).some(key => {
        const initialValue = initialData?.[key];
        const currentValue = formData[key];

        // For new forms (no initialData), only consider it changed if the field is not empty
        if (!initialData) {
          if (typeof currentValue === 'boolean') {
            return currentValue !== false; // Only consider true values as changes
          }
          if (typeof currentValue === 'number') {
            return currentValue !== 0;
          }
          return currentValue !== '' && currentValue !== null;
        }
        
        // For edit forms, compare with initial value
        return initialValue !== currentValue;
      });
      
      onFormChange(hasChanges);
    }
  }, [formData, initialData, onFormChange]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setIsSubmitting(true);
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Category Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand ${
            errors.name ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter category name"
        />
        {errors.name && (
          <p className="text-red-500 text-sm mt-1">{errors.name}</p>
        )}
      </div>

      <div className="flex justify-end space-x-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 bg-brand text-white rounded-md hover:bg-brand-hover disabled:opacity-50 font-semibold transition-colors"
        >
          {isSubmitting ? 'Creating...' : 'Create Category'}
        </button>
      </div>
    </form>
  );
};

export default CategoryForm;